import { Injectable } from '@angular/core'
import { Amplify } from 'aws-amplify'
import { signIn, signOut, fetchAuthSession } from 'aws-amplify/auth'
import { cognitoUserPoolsTokenProvider } from 'aws-amplify/auth/cognito'
import { CookieStorage } from 'aws-amplify/utils'
import { agentPoolId, agentPoolWebClientId, cognitoProxy, mainDomain, poolId, poolWebClientId, identityPoolId, ssoSecure, ssoSameSite } from '../common/utilities/constants'
import { CommonService } from '../common/services/common.service'

type SameSite = 'lax' | 'strict' | 'none'

@Injectable({
  providedIn: 'root'
})
export class AgentAuthService {
  constructor (private commonService: CommonService) {}

  async agentSignIn (username: string, password: string): Promise<any> {
    // Sign out any existing TT session
    try {
      await signOut({ global: false })
    } catch (e) {
      // Ignore if no session exists
    }

    // Clear existing TT Cognito cookies
    this.clearCognitoCookies(poolWebClientId)

    // Reconfigure Amplify to RP pool for signIn
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolEndpoint: cognitoProxy,
          userPoolId: agentPoolId,
          userPoolClientId: agentPoolWebClientId
        }
      }
    })
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
      new CookieStorage({
        domain: mainDomain,
        path: '/',
        secure: ssoSecure,
        sameSite: ssoSameSite as SameSite | undefined
      })
    )

    // Perform sign in
    const result = await signIn({ username, password })

    if (result?.isSignedIn) {
      // Mark that the agent has verified within the current browser session.
      // sessionStorage is scoped to the tab/session, so returning from the
      // speaking page within the same session keeps this flag (skip verify),
      // while a fresh initial visit will not have it (show verify).
      sessionStorage.setItem('agentVerified', 'true')
      // Fetch the session tokens and manually write cookies with RP client ID key
      const session = await fetchAuthSession({ forceRefresh: false })
      const idToken = session?.tokens?.idToken?.toString()
      const accessToken = session?.tokens?.accessToken?.toString()

      if (idToken && accessToken) {
        const lastAuthUser = username
        this.setRpCookie(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.LastAuthUser`, lastAuthUser)
        this.setRpCookie(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.${lastAuthUser}.idToken`, idToken)
        this.setRpCookie(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.${lastAuthUser}.accessToken`, accessToken)
        this.setRpCookie(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.${lastAuthUser}.clockDrift`, '0')

        // Get refreshToken from the TT-keyed cookies (Amplify wrote it there)
        const refreshToken = this.getCookieValue(`CognitoIdentityServiceProvider.${poolWebClientId}.${lastAuthUser}.refreshToken`)
        if (refreshToken) {
          this.setRpCookie(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.${lastAuthUser}.refreshToken`, refreshToken)
        }

        // Copy signInDetails
        const signInDetails = this.getCookieValue(`CognitoIdentityServiceProvider.${poolWebClientId}.${lastAuthUser}.signInDetails`)
        if (signInDetails) {
          this.setRpCookie(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.${lastAuthUser}.signInDetails`, signInDetails)
        }

        // Now clear the TT-keyed cookies since we have everything under RP keys
        this.clearCognitoCookies(poolWebClientId)
      }
    }

    return result
  }

  /**
   * Returns true when a signed-in agent session already exists, i.e. the
   * RP-keyed Cognito cookies (written on agentSignIn) are still present.
   * Cleared on signOut / when the session is cleared, so returning from the
   * speaking page with an active session can skip agent verification.
   */
  hasAgentSession (): boolean {
    // Only treat the session as active if the agent verified during this
    // browser session AND the RP-keyed Cognito tokens are still present.
    if (sessionStorage.getItem('agentVerified') !== 'true') {
      return false
    }
    const lastAuthUser = this.getCookieValue(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.LastAuthUser`)
    if (!lastAuthUser) {
      return false
    }
    const accessToken = this.getCookieValue(`CognitoIdentityServiceProvider.${agentPoolWebClientId}.${lastAuthUser}.accessToken`)
    return !!accessToken
  }

  async signOut (): Promise<void> {
    try {
      await signOut({ global: false })
    } catch (e) {
      // Ignore if no session exists
    }
    this.commonService.clearAgentSession()
    // Clear RP-keyed cognito cookies
    this.clearCognitoCookies(agentPoolWebClientId)
    // Restore default TT Amplify configuration
    this.restoreDefaultConfig()
  }

  restoreDefaultConfig (): void {
    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolEndpoint: cognitoProxy,
          userPoolId: poolId,
          userPoolClientId: poolWebClientId,
          identityPoolId
        }
      }
    })
    cognitoUserPoolsTokenProvider.setKeyValueStorage(
      new CookieStorage({
        domain: mainDomain,
        path: '/',
        secure: ssoSecure,
        sameSite: ssoSameSite as SameSite | undefined
      })
    )
  }

  private setRpCookie (name: string, value: string): void {
    const secure = ssoSecure ? '; Secure' : ''
    const sameSite = ssoSameSite ? `; SameSite=${ssoSameSite}` : ''
    document.cookie = `${name}=${encodeURIComponent(value)}; domain=${mainDomain}; path=/${secure}${sameSite}`
  }

  private getCookieValue (name: string): string | null {
    const cookies = document.cookie.split('; ')
    for (const cookie of cookies) {
      const [key, ...valueParts] = cookie.split('=')
      if (key === name) {
        return decodeURIComponent(valueParts.join('=')) || null
      }
    }
    return null
  }

  private clearCognitoCookies (clientId: string): void {
    const cookies = document.cookie.split('; ')
    for (const cookie of cookies) {
      const name = cookie.split('=')[0]
      if (name.includes(`CognitoIdentityServiceProvider.${clientId}`)) {
        document.cookie = `${name}=; domain=${mainDomain}; path=/; Max-Age=0;`
        document.cookie = `${name}=; path=/; Max-Age=0;`
      }
    }
  }
}
